import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { PartnerForm, BLANK_PARTNER as blank } from "./partners/PartnerForm";
import { LicensePanel } from "./partners/LicensePanel";
import { PartnerDetail } from "./partners/PartnerSections";

const rateCard = (rc) => (rc ? `${Number(rc.per_customer_month) || 0}/cust · ${Number(rc.per_review) || 0}/rev · ${Number(rc.per_1k_api_calls) || 0}/1k` : "—");

function PartnerRow({ t, onOpen, onEdit, onArchive }) {
  return (
    <tr data-testid={`partner-row-${t.id}`}><td className="text-slate-100">{t.name}</td><td className="font-mono">{t.kind}</td><td className="font-mono">{t.deployment}</td><td>{t.branding?.product_name || "—"}</td><td>{t.customers}</td><td>{t.api_keys}</td>
      <td className="font-mono text-slate-400">{rateCard(t.rate_card)}</td><td>{t.active ? "yes" : "no"}</td>
      <td className="whitespace-nowrap"><button onClick={() => onOpen(t)} className="text-amber-400 mr-3" data-testid={`open-partner-${t.id}`}>Keys & usage</button>{t.id !== "platform" && <><button onClick={() => onEdit(t)} className="text-slate-300 mr-3">Edit</button><button onClick={() => onArchive(t)} className="text-red-400" data-testid={`archive-partner-${t.id}`}>Archive</button></>}</td></tr>
  );
}

export default function PlatformPartners() {
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = async () => { setLoading(true); try { const { data } = await api.get("/platform/partners"); setList(data); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);
  const archive = async (t) => { if (!window.confirm(`Archive "${t.name}"? All its API keys are revoked; customer data is kept.`)) return; await api.delete(`/platform/partners/${t.id}`); toast.success("Partner archived"); load(); };
  const save = async () => {
    const p = { ...edit, rate_card: Object.fromEntries(Object.entries(edit.rate_card || {}).map(([k, v]) => [k, Number(v) || 0])) };
    if (!p.contact_email) delete p.contact_email;
    try {
      if (p.id) await api.put(`/platform/partners/${p.id}`, p); else await api.post("/platform/partners", p);
      toast.success("Partner saved"); setEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Failed"); }
  };
  const startEdit = (t) => setEdit({ ...blank, ...t, branding: { ...blank.branding, ...(t.branding || {}) }, rate_card: { ...blank.rate_card, ...(t.rate_card || {}) } });
  const rows = useMemo(() => list.map((t) => <PartnerRow key={t.id} t={t} onOpen={setOpen} onEdit={startEdit} onArchive={archive} />), [list]); // eslint-disable-line react-hooks/exhaustive-deps -- handlers are stable per render of list
  return (
    <>
      <div className="sc-card p-5" data-testid="partners-card">
        <div className="flex items-center justify-between mb-3">
          <div className="label-cap">Tenants — direct customers, OEM / white-label partners, integrators</div>
          <button onClick={() => setEdit({ ...blank })} className="text-xs text-amber-400 inline-flex items-center gap-1" data-testid="new-partner-button"><Plus className="w-3.5 h-3.5" /> New partner</button>
        </div>
        <table className="sc-table w-full text-xs"><thead><tr><th>Tenant</th><th>Kind</th><th>Deployment</th><th>White-label name</th><th>Customers</th><th>Keys</th><th>Rate card</th><th>Active</th><th></th></tr></thead>
          <tbody>{rows}{loading && !list.length && <tr><td colSpan={9} className="text-slate-500 animate-pulse">Loading tenants…</td></tr>}</tbody></table>
        {edit && <PartnerForm value={edit} onChange={setEdit} onSave={save} onCancel={() => setEdit(null)} />}
        {open && <PartnerDetail t={open} onClose={() => setOpen(null)} onPurged={load} />}
      </div>
      <LicensePanel />
    </>
  );
}
